import React, { useState } from 'react'
import {
  Box,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Divider,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from '@mui/material'
import {
  Payment as PaymentIcon,
  CreditCard as CreditCardIcon,
  AccountBalance as BankIcon,
  CurrencyBitcoin as CryptoIcon,
  Settings as SettingsIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Security as SecurityIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material'

const PaymentSettings = ({ settings = {}, onChange, isLoading = false }) => {
  const [localSettings, setLocalSettings] = useState({
    // Payment methods
    enableCreditCards: true,
    enableBankTransfer: true,
    enableCrypto: false,
    enablePaypal: false,
    
    // Credit card settings
    stripePublicKey: '',
    stripeSecretKey: '',
    stripeWebhookSecret: '',
    
    // PayPal settings
    paypalClientId: '',
    paypalClientSecret: '',
    paypalSandbox: true,
    
    // Bank transfer settings
    bankName: 'Türkiye İş Bankası',
    bankAccountName: 'VirtualSMS Ltd.',
    bankAccountNumber: 'TR33 0006 4000 0011 2345 6789 01',
    bankSwiftCode: 'ISBKTRIS',
    
    // Cryptocurrency settings
    btcWalletAddress: '',
    ethWalletAddress: '',
    usdtWalletAddress: '',
    
    // Pricing settings
    currency: 'TRY',
    vatRate: 20,
    enableVAT: true,
    minimumPayment: 10,
    maximumPayment: 10000,
    
    // Commission settings
    creditCardFee: 3.5,
    bankTransferFee: 0,
    cryptoFee: 1,
    paypalFee: 4,
    
    // Auto-processing
    autoProcessPayments: true,
    autoRefunds: false,
    paymentTimeout: 24,
    
    ...settings
  })

  const [exchangeRates] = useState([
    { from: 'USD', to: 'TRY', rate: 27.5 },
    { from: 'EUR', to: 'TRY', rate: 29.8 },
    { from: 'GBP', to: 'TRY', rate: 34.2 },
  ])

  const handleSettingChange = (key, value) => {
    setLocalSettings(prev => ({
      ...prev,
      [key]: value
    }))
    onChange?.()
  }

  const paymentMethods = [
    {
      key: 'enableCreditCards',
      icon: <CreditCardIcon />,
      title: 'Kredi Kartı',
      description: 'Visa, Mastercard, American Express',
      color: 'primary',
      enabled: localSettings.enableCreditCards
    },
    {
      key: 'enableBankTransfer',
      icon: <BankIcon />,
      title: 'Banka Havalesi',
      description: 'EFT/Havale ile ödeme',
      color: 'info',
      enabled: localSettings.enableBankTransfer
    },
    {
      key: 'enablePaypal',
      icon: <PaymentIcon />,
      title: 'PayPal',
      description: 'PayPal hesabı ile ödeme',
      color: 'warning',
      enabled: localSettings.enablePaypal
    },
    {
      key: 'enableCrypto',
      icon: <CryptoIcon />,
      title: 'Kripto Para',
      description: 'Bitcoin, Ethereum, USDT',
      color: 'success',
      enabled: localSettings.enableCrypto
    }
  ]

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
          Ödeme Ayarları
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Ödeme yöntemleri ve finansal ayarları yapılandırın
        </Typography>
      </Box>

      {/* Payment Methods */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <PaymentIcon />
            Ödeme Yöntemleri
          </Typography>
          
          <Grid container spacing={2}>
            {paymentMethods.map((method) => (
              <Grid item xs={12} md={6} key={method.key}>
                <Paper sx={{ p: 2, border: 1, borderColor: method.enabled ? `${method.color}.main` : 'divider' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ display: 'flex', gap: 2, flex: 1 }}>
                      <Box sx={{ color: method.enabled ? `${method.color}.main` : 'text.secondary' }}>
                        {method.icon}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {method.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {method.description}
                        </Typography>
                        {method.enabled && (
                          <Chip 
                            label="Aktif" 
                            color={method.color} 
                            size="small" 
                            sx={{ mt: 1 }} 
                          />
                        )}
                      </Box>
                    </Box>
                    <Switch
                      checked={method.enabled}
                      onChange={(e) => handleSettingChange(method.key, e.target.checked)}
                      color={method.color}
                      size="small"
                    />
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Stripe Configuration */}
      {localSettings.enableCreditCards && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CreditCardIcon />
              Stripe Yapılandırması
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Stripe Public Key"
                  value={localSettings.stripePublicKey}
                  onChange={(e) => handleSettingChange('stripePublicKey', e.target.value)}
                  helperText="Stripe genel anahtarı (pk_live_ ile başlar)"
                  placeholder="pk_live_..."
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Stripe Secret Key"
                  type="password"
                  value={localSettings.stripeSecretKey}
                  onChange={(e) => handleSettingChange('stripeSecretKey', e.target.value)}
                  helperText="Stripe gizli anahtarı (sk_live_ ile başlar)"
                  placeholder="sk_live_..."
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Webhook Secret"
                  type="password"
                  value={localSettings.stripeWebhookSecret}
                  onChange={(e) => handleSettingChange('stripeWebhookSecret', e.target.value)}
                  helperText="Stripe webhook imzalama anahtarı"
                  placeholder="whsec_..."
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Kredi Kartı İşlem Ücreti (%)"
                  type="number"
                  value={localSettings.creditCardFee}
                  onChange={(e) => handleSettingChange('creditCardFee', parseFloat(e.target.value) || 0)}
                  helperText="Kredi kartı ödemelerinde alınacak komisyon oranı"
                  inputProps={{ min: 0, max: 10, step: 0.1 }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* PayPal Configuration */}
      {localSettings.enablePaypal && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <PaymentIcon />
              PayPal Yapılandırması
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="PayPal Client ID"
                  value={localSettings.paypalClientId}
                  onChange={(e) => handleSettingChange('paypalClientId', e.target.value)}
                  helperText="PayPal uygulama client ID'si"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="PayPal Client Secret"
                  type="password"
                  value={localSettings.paypalClientSecret}
                  onChange={(e) => handleSettingChange('paypalClientSecret', e.target.value)}
                  helperText="PayPal uygulama client secret"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={localSettings.paypalSandbox}
                      onChange={(e) => handleSettingChange('paypalSandbox', e.target.checked)}
                    />
                  }
                  label="Sandbox Modu (Test Ortamı)"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="PayPal İşlem Ücreti (%)"
                  type="number"
                  value={localSettings.paypalFee}
                  onChange={(e) => handleSettingChange('paypalFee', parseFloat(e.target.value) || 0)}
                  helperText="PayPal ödemelerinde alınacak komisyon oranı"
                  inputProps={{ min: 0, max: 10, step: 0.1 }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Bank Transfer Configuration */}
      {localSettings.enableBankTransfer && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <BankIcon />
              Banka Hesap Bilgileri
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Banka Adı"
                  value={localSettings.bankName}
                  onChange={(e) => handleSettingChange('bankName', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Hesap Sahibi"
                  value={localSettings.bankAccountName}
                  onChange={(e) => handleSettingChange('bankAccountName', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="IBAN"
                  value={localSettings.bankAccountNumber}
                  onChange={(e) => handleSettingChange('bankAccountNumber', e.target.value)}
                  helperText="TR ile başlayan IBAN numarası"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="SWIFT Kodu"
                  value={localSettings.bankSwiftCode}
                  onChange={(e) => handleSettingChange('bankSwiftCode', e.target.value)}
                  helperText="Uluslararası havale için SWIFT kodu"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Cryptocurrency Configuration */}
      {localSettings.enableCrypto && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CryptoIcon />
              Kripto Para Cüzdan Adresleri
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Bitcoin (BTC) Cüzdan Adresi"
                  value={localSettings.btcWalletAddress}
                  onChange={(e) => handleSettingChange('btcWalletAddress', e.target.value)}
                  helperText="Bitcoin ödemeleri için cüzdan adresi"
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Ethereum (ETH) Cüzdan Adresi"
                  value={localSettings.ethWalletAddress}
                  onChange={(e) => handleSettingChange('ethWalletAddress', e.target.value)}
                  helperText="Ethereum ödemeleri için cüzdan adresi"
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="USDT Cüzdan Adresi"
                  value={localSettings.usdtWalletAddress}
                  onChange={(e) => handleSettingChange('usdtWalletAddress', e.target.value)}
                  helperText="USDT ödemeleri için cüzdan adresi"
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Kripto Para İşlem Ücreti (%)"
                  type="number"
                  value={localSettings.cryptoFee}
                  onChange={(e) => handleSettingChange('cryptoFee', parseFloat(e.target.value) || 0)}
                  helperText="Kripto para ödemelerinde alınacak komisyon oranı"
                  inputProps={{ min: 0, max: 10, step: 0.1 }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Pricing Settings */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReceiptIcon />
            Fiyatlandırma Ayarları
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Ana Para Birimi</InputLabel>
                <Select
                  value={localSettings.currency}
                  label="Ana Para Birimi"
                  onChange={(e) => handleSettingChange('currency', e.target.value)}
                >
                  <MenuItem value="TRY">Türk Lirası (₺)</MenuItem>
                  <MenuItem value="USD">US Dollar ($)</MenuItem>
                  <MenuItem value="EUR">Euro (€)</MenuItem>
                  <MenuItem value="GBP">British Pound (£)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Minimum Ödeme Tutarı"
                type="number"
                value={localSettings.minimumPayment}
                onChange={(e) => handleSettingChange('minimumPayment', parseFloat(e.target.value) || 0)}
                helperText={`${localSettings.currency} cinsinden minimum tutar`}
                inputProps={{ min: 1, max: 1000 }}
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Maksimum Ödeme Tutarı"
                type="number"
                value={localSettings.maximumPayment}
                onChange={(e) => handleSettingChange('maximumPayment', parseFloat(e.target.value) || 0)}
                helperText={`${localSettings.currency} cinsinden maksimum tutar`}
                inputProps={{ min: 100, max: 100000 }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={localSettings.enableVAT}
                    onChange={(e) => handleSettingChange('enableVAT', e.target.checked)}
                  />
                }
                label="KDV Dahil Fiyatlandırma"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="KDV Oranı (%)"
                type="number"
                value={localSettings.vatRate}
                onChange={(e) => handleSettingChange('vatRate', parseFloat(e.target.value) || 0)}
                disabled={!localSettings.enableVAT}
                helperText="Türkiye için standart KDV oranı %20"
                inputProps={{ min: 0, max: 50, step: 1 }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Exchange Rates */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Döviz Kurları
          </Typography>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Para Birimi</TableCell>
                  <TableCell>Hedef</TableCell>
                  <TableCell align="right">Kur</TableCell>
                  <TableCell align="center">İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {exchangeRates.map((rate, index) => (
                  <TableRow key={index}>
                    <TableCell>{rate.from}</TableCell>
                    <TableCell>{rate.to}</TableCell>
                    <TableCell align="right">{rate.rate.toFixed(2)}</TableCell>
                    <TableCell align="center">
                      <IconButton size="small">
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" color="error">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Button
            startIcon={<AddIcon />}
            sx={{ mt: 2 }}
          >
            Yeni Kur Ekle
          </Button>
        </CardContent>
      </Card>

      {/* Processing Settings */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <SettingsIcon />
            İşlem Ayarları
          </Typography>
          
          <List>
            <ListItem>
              <ListItemText
                primary="Otomatik Ödeme İşleme"
                secondary="Onaylanan ödemeler otomatik olarak hesaba yansıtılsın"
              />
              <ListItemSecondaryAction>
                <Switch
                  checked={localSettings.autoProcessPayments}
                  onChange={(e) => handleSettingChange('autoProcessPayments', e.target.checked)}
                  color="success"
                />
              </ListItemSecondaryAction>
            </ListItem>
            
            <Divider />
            
            <ListItem>
              <ListItemText
                primary="Otomatik İade"
                secondary="Başarısız işlemler için otomatik iade yapılsın"
              />
              <ListItemSecondaryAction>
                <Switch
                  checked={localSettings.autoRefunds}
                  onChange={(e) => handleSettingChange('autoRefunds', e.target.checked)}
                  color="warning"
                />
              </ListItemSecondaryAction>
            </ListItem>
          </List>
          
          <Box sx={{ mt: 3 }}>
            <TextField
              fullWidth
              label="Ödeme Zaman Aşımı (saat)"
              type="number"
              value={localSettings.paymentTimeout}
              onChange={(e) => handleSettingChange('paymentTimeout', parseInt(e.target.value) || 24)}
              helperText="Bekleyen ödemelerin zaman aşımı süresi"
              inputProps={{ min: 1, max: 168 }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Security Warnings */}
      <Box sx={{ mb: 3 }}>
        {(!localSettings.stripeSecretKey && localSettings.enableCreditCards) && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Uyarı:</strong> Kredi kartı ödemeleri etkin ancak Stripe anahtarları yapılandırılmamış.
            </Typography>
          </Alert>
        )}

        {(!localSettings.paypalClientSecret && localSettings.enablePaypal) && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Uyarı:</strong> PayPal ödemeleri etkin ancak PayPal anahtarları yapılandırılmamış.
            </Typography>
          </Alert>
        )}

        {localSettings.enableVAT && localSettings.vatRate === 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Hata:</strong> KDV etkin ancak KDV oranı 0%. Lütfen geçerli bir KDV oranı girin.
            </Typography>
          </Alert>
        )}
      </Box>

      {/* Payment Status Summary */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Ödeme Yöntemi Durumu
          </Typography>
          
          <Grid container spacing={2}>
            {paymentMethods.map((method) => (
              <Grid item xs={6} md={3} key={method.key}>
                <Box sx={{ textAlign: 'center' }}>
                  <Chip 
                    label={method.enabled ? "Aktif" : "Pasif"}
                    color={method.enabled ? method.color : "default"}
                    sx={{ mb: 1 }}
                  />
                  <Typography variant="caption" display="block">
                    {method.title}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    </Box>
  )
}

export default PaymentSettings